import type { PayloadAction } from '@reduxjs/toolkit';
import { combineReducers, createSlice } from '@reduxjs/toolkit';
import type { RequestStatus, GenericError } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { TaskState, TradingTasksCommands } from './types';
import type { JsonTradingTask } from './types';
import { cancelTask, pauseTask, resumeTask, updateTask } from './actions';
import { actionEsOnMessage } from '@skynet/data-ws-es';
import { describeWsFailure } from 'src/store/utils';
import { createPersistedFilterOptionsSlice } from '@skynet/data-filter-options';
import { withResetState } from 'src/store/actions';
import storage from 'src/store/storage';
import type { FilterOptions } from '@skynet/data-filter-options';

export interface TradingTasksState {
  list: JsonTradingTask[]; // Replace 'any' with a more specific type if available
  requestStatus: RequestStatus;
  error: null | GenericError;
  resPauseTaskStatus: RequestStatus;
  resResumeTaskStatus: RequestStatus;
  resCancelTaskStatus: RequestStatus;

  resEditTaskStatus: RequestStatus;
  payloadEditTask: JsonTradingTask | null;
}

export const initialState: TradingTasksState = {
  list: [],
  requestStatus: null,
  error: null,
  resPauseTaskStatus: null,
  resResumeTaskStatus: null,
  resCancelTaskStatus: null,
  resEditTaskStatus: null,
  payloadEditTask: null, // Stores the task being edited in the modal
};

const allowedCommands = Object.values(TradingTasksCommands);

registerErrorMessage(TradingTasksCommands.Tasks, {
  title: 'Tasks Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingTasksCommands.PauseTask, {
  title: 'Tasks Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingTasksCommands.ResumeTask, {
  title: 'Tasks Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingTasksCommands.CancelTask, {
  title: 'Tasks Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingTasksCommands.UpdateTask, {
  title: 'Task Update Error',
  getDescription: describeWsFailure,
});

const tradingTasksSlice = createSlice({
  name: 'tradingTasks',
  initialState: initialState,
  reducers: {
    setPayloadEditTask: (state, action: PayloadAction<JsonTradingTask | null>) => {
      state.payloadEditTask = action.payload;
      state.resEditTaskStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(pauseTask.pending, (state) => {
      state.resPauseTaskStatus = 'pending';
    });
    builder.addCase(resumeTask.pending, (state) => {
      state.resResumeTaskStatus = 'pending';
    });
    builder.addCase(cancelTask.pending, (state) => {
      state.resCancelTaskStatus = 'pending';
    });

    builder.addCase(updateTask.pending, (state) => {
      state.resEditTaskStatus = 'pending';
    });

    builder.addCase(actionEsOnMessage, (state, action) => {
      const { json, skipProcessing, error, okCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command, errorCommand } = json;

      if (error) {
        state.error = error;
        if (errorCommand === TradingTasksCommands.Tasks) {
          state.requestStatus = 'error';
        }
        switch (errorCommand) {
          case TradingTasksCommands.UpdateTask:
            state.resEditTaskStatus = 'error';
            break;
          default:
        }
        return;
      }

      if (isOkCommand(command)) {
        switch (okCommand) {
          case TradingTasksCommands.PauseTask:
            state.resPauseTaskStatus = 'success';
            break;
          case TradingTasksCommands.ResumeTask:
            state.resResumeTaskStatus = 'success';
            break;
          case TradingTasksCommands.CancelTask:
            state.resCancelTaskStatus = 'success';
            break;

          case TradingTasksCommands.UpdateTask:
            state.resEditTaskStatus = 'success';
            state.payloadEditTask = null;
            break;

          case undefined:
          default:
        }
      }

      if (command === TradingTasksCommands.Tasks) {
        const { list } = json;
        state.list = list;
        state.requestStatus = 'success';
        state.resPauseTaskStatus = null;
        state.resResumeTaskStatus = null;
        state.resCancelTaskStatus = null;
      }
    });
  },
});

export interface TaskFilterOptions {
  token: string[];
  action: string[];
  state: string[];
  type: string[];
}

export type TaskFilterState = FilterOptions<TaskFilterOptions>;

export const initialFilterOptions: TaskFilterOptions = {
  token: [],
  action: [],
  state: [TaskState.Active.toString(), TaskState.RecentlyFinished.toString()],
  type: [],
};

const taskFilterOptionsSlice = createPersistedFilterOptionsSlice<TaskFilterOptions>({
  prefix: 'tradingTasks',
  initialState: initialFilterOptions,
  optionsPersist: {},
  storage,
  sortDefault: 'created:desc',
  clearState: {
    ...initialFilterOptions,
    state: [],
  },
});

export const tasksActions = {
  ...taskFilterOptionsSlice.actions,
  ...tradingTasksSlice.actions,
  updateTask,
  cancelTask,
  pauseTask,
  resumeTask,
};

export default combineReducers({
  data: withResetState(tradingTasksSlice.reducer, initialState),
  filter: taskFilterOptionsSlice.reducer,
});
